import React, { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Globe2, ShieldBan, ShieldCheck } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { ADMIN_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';

const AdminRestrictions = () => {
    const [type, setType] = useState("country");
    const [country, setCountry] = useState("");
    const [region, setRegion] = useState("");
    const [busy, setBusy] = useState(false);
    const [log, setLog] = useState([]);

    const { role } = useOutletContext() || {};
    const isAdmin = role === "admin";

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    const submit = async (functionality) => {
        if (!country.trim() || (type === "region" && !region.trim())) {
            pushAlert("Enter a country code, and a region if you're restricting by region.");
            return;
        }

        setBusy(true);
        try {
            const res = await fetch(ADMIN_URL + "/restrict-area", {
                method: "POST",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    country: country.trim().toUpperCase(),
                    region: region.trim(),
                    type,
                    functionality,
                }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while updating restrictions.");
                return;
            }

            setLog(prev => ([
                { id: v4(), type, country: country.trim().toUpperCase(), region: region.trim(), functionality, at: Date.now() },
                ...prev,
            ]));
            pushAlert(data.message || "Restriction updated successfully.");
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/admin")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Restricted Areas</h2>
                    <p>Block or unblock access by country or region.</p>
                </div>
            </div>

            <div className="page_body">
                {!isAdmin ? (
                    <div className="viewer_note">Viewer mode — this section is admin-only.</div>
                ) : (
                    <>
                        <div className="edit_card">
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div className="field_container">
                                    <label>Restriction Type</label>
                                    <select className="select_input" value={type} onChange={(e) => setType(e.target.value)}>
                                        <option value="country">Entire Country</option>
                                        <option value="region">Specific Region</option>
                                    </select>
                                </div>
                                <div className="field_container">
                                    <label>Country Code</label>
                                    <div className="input"><input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. EG" maxLength={2} required /></div>
                                    <span className="field_hint">2-letter ISO country code, matches your geoip lookup.</span>
                                </div>
                                {type === "region" ? (
                                    <div className="field_container">
                                        <label>Region</label>
                                        <div className="input"><input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Beheira" required /></div>
                                    </div>
                                ) : null}
                                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                                    <button type="button" className="pill_button danger" style={{ flex: 1 }} disabled={busy} onClick={() => submit("restrict")}>
                                        {busy ? <span className="loading"></span> : <><ShieldBan size="16" /> Restrict</>}
                                    </button>
                                    <button type="button" className="pill_button ghost" style={{ flex: 1 }} disabled={busy} onClick={() => submit("unrestrict")}>
                                        {busy ? <span className="loading"></span> : <><ShieldCheck size="16" /> Unrestrict</>}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {log.length > 0 ? (
                            <div className="profile_section">
                                <div className="section_title">This Session</div>
                                <div className="devices_list">
                                    {log.map(entry => (
                                        <div className="restriction_row" key={entry.id}>
                                            <div className="device_meta">
                                                <h4><Globe2 size="12" style={{ verticalAlign: "-1px" }} /> {entry.country}{entry.region ? ` / ${entry.region}` : ""}</h4>
                                                <span>{new Date(entry.at).toLocaleTimeString()}</span>
                                            </div>
                                            <span className={`badge ${entry.functionality === "restrict" ? "banned" : "active"}`}>
                                                {entry.functionality === "restrict" ? "Restricted" : "Unrestricted"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    )
}

export default AdminRestrictions;
